import { Button, CircularProgress, IconButton, Tooltip } from '@civilgrid/ui/components';
import { useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { Row, Table } from '@tanstack/react-table';
import { bbox } from '@turf/turf';
import classNames from 'classnames';
import React, { type ReactElement, useMemo, useRef, useState } from 'react';
import { Popup, useMap } from 'react-map-gl/mapbox';
import { useAsyncFn } from 'react-use';
import { useSnapshot } from 'valtio';
import { getDrfRequestActivities as apiGetActivities, updateDrfRequest as apiUpdateRequest } from '~/api/drf';
import { drfStatusLabels, paymentMethodLabels, requestFlagLabels } from '~/common/drfUtils';
import { useQuery } from '~/common/routerUtils';
import { type Activity, DrfRequestStatus, type MapFulfillmentRequest, MapStyle } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import Input from '~/components2/forms/Input';
import TextArea from '~/components2/forms/TextArea';
import { MapComponent } from '~/components2/Map';
import { Notify } from '~/components2/Notify';
import Sheet from '~/components2/Sheet';
import { useIsMounted } from '~/hooks/useIsMounted';
import rootStore from '~/store';
import FileViewer from '../admin/civilsearch/requests/requestDetails/tabs/supportingFiles/FileViewer';
import {
  ActivityFeedItem,
  BoundaryNote,
  ExportGeometryToKMZButton,
  FormSectionTitle,
  resetFormValues,
  StatusButtons,
  StatusSelect,
} from './common';
import CriticalInfraPolygons from './criticalInfrastructure/CriticalInfraPolygons';
import { downloadCeiiFile } from './downloadCeiiFile';

type BBox = [number, number, number, number];
interface Props {
  table: Table<MapFulfillmentRequest>;
  setData: React.Dispatch<React.SetStateAction<MapFulfillmentRequest[]>>;
  isCriticalInfraVisible: boolean;
}

type M = Row<MapFulfillmentRequest> | undefined;
type C = {
  previous: M;
  current: M;
  next: M;
  rowCount: number;
  currentIndex: number;
};

interface PopupProps {
  lngLat: { lng: number; lat: number };
  content: ReactElement;
}

const MAP_ID = 'dataRequestFulfillmentMap';
type Timer = ReturnType<typeof setTimeout> | null;

function resetTimer(timer: Timer) {
  if (timer) {
    clearTimeout(timer);
  }
}

const results = {
  success: 'success',
  error: 'error',
  indeterminate: 'indeterminate',
} as const;

type Result = keyof typeof results;

const useDebouncedUpdate = ({
  setValue,
  config,
  setData,
  property,
  onSuccess,
}: {
  setValue: (value: React.SetStateAction<string>) => void;
  config: C;
  setData: Props['setData'];
  property: keyof MapFulfillmentRequest;
  onSuccess?: () => Promise<any>;
}) => {
  const [result, setResult] = useState<Result>(results.indeterminate);
  const isMounted = useIsMounted();
  const timer = useRef<Timer>(null);

  const [update, fetchUpdate] = useAsyncFn(
    async (updates: Partial<MapFulfillmentRequest>) => {
      if (!config.current) throw new Error('No item found!');
      const { current } = config;
      const requestId = current.id;

      const res = await apiUpdateRequest(requestId, { ...updates });

      if (!res.id) {
        Notify.error({
          message: 'An error occurred while updating the request, please contact support.',
        });
        throw new Error('Bad response!');
      }

      setData((prev) => {
        const copy = [...prev];
        let matched = copy[current.index];
        if (matched) {
          matched = {
            ...matched,
            ...res,
          };
          copy[current.index] = matched;
        }

        return copy;
      });

      if (onSuccess) {
        await onSuccess();
      }

      return res;
    },
    [config.current?.id],
  );

  const onChange = (e: string) => {
    setResult(results.indeterminate);
    setValue(e);
    resetTimer(timer.current);

    timer.current = setTimeout(async () => {
      await fetchUpdate({ [property]: e });
      setResult(results.success);

      setTimeout(() => {
        if (isMounted()) {
          setResult(results.indeterminate);
        }
      }, 1_000);
    }, 750);
  };

  const isLoading = !!update.loading;
  const isError = !!update.error || result === 'error';
  const isSuccess = !update.loading && !isError && result === results.success;

  return {
    update,
    fetchUpdate,
    onChange,
    isLoading,
    isError,
    isSuccess,
  };
};

export default function RequestSheet({ table, setData, isCriticalInfraVisible }: Props) {
  const { currentOrganization } = useSnapshot(rootStore);
  const navigate = useNavigate();
  const requestId = useQuery().get('requestId');
  const renderedMaps = useMap();
  const search = useQuery();
  const [internalNotes, setInternalNotes] = useState<string>('');
  const [refId, setRefId] = useState<string>('');
  const [sharedLinkUrl, setSharedLinkUrl] = useState<string>('');
  const [popup, setPopup] = useState<null | PopupProps>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const config = useMemo<C>(() => {
    let previous: M;
    let current: M;
    let next: M;
    let rowCount: number = 0;
    let currentIndex: number = 0;

    if (requestId) {
      const rows = table.getCoreRowModel().rows;

      rowCount = rows.length;
      currentIndex = rows.findIndex((row) => row.id === requestId);

      if (currentIndex === -1) {
        currentIndex = 0;
      }

      if (currentIndex || currentIndex === 0) {
        previous = rows[currentIndex - 1];
        current = rows[currentIndex];
        next = rows[currentIndex + 1];
      }
    }

    return { previous, current, next, rowCount, currentIndex };
  }, [requestId, table.getCoreRowModel()]);

  const activitiesQuery = useTanstackQuery({
    enabled: !!config.current?.id && !!currentOrganization?.id,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['drf-request-activities', config.current?.id],
    queryFn: async () => await apiGetActivities(config.current!.id, currentOrganization!.id),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const values = useMemo(() => {
    setInternalNotes(config?.current?.original?.notes ?? '');
    setRefId(config?.current?.original?.externalRefId ?? '');
    setSharedLinkUrl(config?.current?.original?.sharedLinkUrl ?? '');
    return resetFormValues(config?.current?.original);
  }, [config?.current?.id]);

  const { previous, current, next } = config;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const initialViewState = useMemo(() => {
    return {
      bounds: current?.id ? (bbox(current.original.geometry) as BBox) : undefined,
      fitBoundsOptions: { padding: 50 },
    };
  }, [current?.id]);

  const activityItems = useMemo(() => {
    const feed: React.ReactNode[] = [];

    const request = current?.original;

    if (activitiesQuery.isFetching) {
      return <CircularProgress indeterminate />;
    } else if (activitiesQuery.isError) {
      return (
        <div>
          An error occurred while fetching the activity feed, please <EmailLink>contact support</EmailLink>.
        </div>
      );
    } else if (request) {
      feed.push(
        <ActivityFeedItem key={`activity-item-created-by-${request.id}`} date={request.createdAt}>
          Request created by{' '}
          <span className="font-semibold">
            {request.requestor.name} ({request.requestor.email})
          </span>
        </ActivityFeedItem>,
      );

      const itemsToRender: (
        | (MapFulfillmentRequest['statusTransitions'][number] & {
            type: 'statusTransition';
          })
        | (Activity & { type: 'activity' })
      )[] = request.statusTransitions.map((t) => ({
        ...t,
        type: 'statusTransition',
      }));

      if (activitiesQuery.data) {
        activitiesQuery.data.forEach((a) => {
          itemsToRender.push({
            ...a,
            type: 'activity',
          });
        });
      }

      itemsToRender
        .sort((a, b) => a.updatedAt.toMillis() - b.updatedAt.toMillis())
        .forEach((item) => {
          if (item.type === 'statusTransition') {
            if (item.to === DrfRequestStatus.Fulfilled) {
              feed.push(
                <ActivityFeedItem key={`activity-item-status-transition-${item.id}`} date={item.updatedAt}>
                  Request completed by{' '}
                  <span className="font-semibold">
                    {item.updatedBy.name} ({item.updatedBy.email})
                  </span>
                </ActivityFeedItem>,
              );
            } else {
              feed.push(
                <ActivityFeedItem key={`activity-item-status-transition-${item.id}`} date={item.updatedAt}>
                  <span className="font-semibold">
                    {item.updatedBy.name} ({item.updatedBy.email})
                  </span>{' '}
                  changed status from <span className="font-semibold">{drfStatusLabels[item.from]}</span> to{' '}
                  <span className="font-semibold">{drfStatusLabels[item.to]}</span>
                </ActivityFeedItem>,
              );
            }
          } else {
            if (item.activityType === 'drfRequestApprovalPacketGenerated' && !!item.details?.outputPath) {
              feed.push(
                <ActivityFeedItem key={`activity-item-activity-${item.id}`} date={item.createdAt}>
                  <a
                    href={item.details.outputPath}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-50 underline hover:text-blue-60"
                  >
                    Facilities Map packet
                  </a>{' '}
                  sent by CivilGrid
                </ActivityFeedItem>,
              );
            } else if (item.activityType === 'drfRequestExternalRefIdUpdated') {
              // if the external ref id was updated, then we need to show the previous and new values
              // as well as the user who logged the activity
              if (item.details?.previousExternalRefId) {
                // if no NEW external ref ID was provided, then the ID was likely removed
                if (!item.details?.externalRefId) {
                  feed.push(
                    <ActivityFeedItem key={`activity-item-activity-${item.id}`} date={item.updatedAt}>
                      <span className="font-semibold">
                        {item.user?.name} ({item.user?.email})
                      </span>{' '}
                      removed the external ref ID{' '}
                      <span className="font-semibold">{item.details?.previousExternalRefId}</span>
                    </ActivityFeedItem>,
                  );
                } else {
                  feed.push(
                    <ActivityFeedItem key={`activity-item-activity-${item.id}`} date={item.updatedAt}>
                      <span className="font-semibold">
                        {item.user?.name} ({item.user?.email})
                      </span>{' '}
                      updated the external ref ID from{' '}
                      <span className="font-semibold">{item.details!.previousExternalRefId}</span> to{' '}
                      <span className="font-semibold">{item.details!.externalRefId}</span>
                    </ActivityFeedItem>,
                  );
                }
              } else {
                // if no previous ref ID exists, then the ID was likely added during the
                // initial request submission (or the ID was removed and re-added previously)
                // so we need to show the user who added the ID via the requestor information
                feed.push(
                  <ActivityFeedItem key={`activity-item-activity-${item.id}`} date={item.updatedAt}>
                    <span className="font-semibold">
                      {item.user
                        ? `${item.user.name} (${item.user.email})`
                        : `${item.mapFulfillmentRequest?.requestor.name} (${item.mapFulfillmentRequest?.requestor.email})`}
                    </span>{' '}
                    added the external ref ID of <span className="font-semibold">{item.details?.externalRefId}</span>
                  </ActivityFeedItem>,
                );
              }
            }
          }
        });
    }

    return feed;
  }, [current, activitiesQuery]);

  function onNavigate(to: 'next' | 'previous') {
    const item = config[to];
    const id = item?.id;
    if (!id || !item) return;

    const map = renderedMaps[MAP_ID];

    if (map) {
      const bounds = bbox(item.original.geometry) as BBox;
      map.fitBounds(bounds, {
        padding: 50,
        duration: 250,
      });
    }

    const newParams = new URLSearchParams(search);
    newParams.set('requestId', id);

    navigate({ to: `/dataRequestFulfillment?${newParams.toString()}` });
  }

  const {
    onChange: onNotesChange,
    isError: isNotesError,
    isLoading: isNotesLoading,
    isSuccess: isNotesSuccess,
    update: notesUpdate,
    fetchUpdate: fetchUpdateNotes,
  } = useDebouncedUpdate({
    setValue: setInternalNotes,
    config,
    setData,
    property: 'notes',
  });

  const {
    onChange: onRefIdChange,
    isLoading: isRefIdLoading,
    isError: isRefIdError,
    isSuccess: isRefIdSuccess,
  } = useDebouncedUpdate({
    setValue: setRefId,
    config,
    setData,
    property: 'externalRefId',
    onSuccess: activitiesQuery.refetch,
  });

  const {
    onChange: onSharedLinkUrlChange,
    isLoading: isSharedLinkUrlLoading,
    isError: isSharedLinkUrlError,
    isSuccess: isSharedLinkUrlSuccess,
  } = useDebouncedUpdate({
    setValue: setSharedLinkUrl,
    config,
    setData,
    property: 'sharedLinkUrl',
  });

  if (!current) return null;

  return (
    <Sheet
      isOpen
      title={`Request ${current.original.name || current.original.requestId}`}
      isOverlayDismissible={false}
      data-testid="drf-request-sheet"
      onOverlayClick={() => {
        const newParams = new URLSearchParams(search);
        newParams.delete('requestId');
        navigate({ to: `/dataRequestFulfillment?${newParams.toString()}` });
      }}
    >
      {() => {
        return (
          <div className="flex h-full w-full flex-col justify-between space-y-4">
            <div className="grid grid-cols-3">
              <div className="text-left text-lg font-bold">{current.original.name || current.original.requestId}</div>

              <div className="flex items-center justify-center space-x-2">
                <Tooltip asChild text="Previous request">
                  <IconButton
                    icon="chevronLeft"
                    variant="secondary"
                    disabled={!previous}
                    aria-label="Previous request"
                    data-testid="request-sheet-previous"
                    onClick={() => onNavigate('previous')}
                  />
                </Tooltip>

                <div>
                  <span>{config.currentIndex + 1}</span> of <span>{config.rowCount}</span>
                </div>

                <Tooltip asChild text="Next request">
                  <IconButton
                    icon="chevronRight"
                    variant="secondary"
                    disabled={!next}
                    aria-label="Next request"
                    data-testid="request-sheet-next"
                    onClick={() => onNavigate('next')}
                  />
                </Tooltip>
              </div>

              <div className="flex w-full justify-end">
                <CloseIconButton
                  aria-label="Close request"
                  onClick={() => {
                    const newParams = new URLSearchParams(search);
                    newParams.delete('requestId');
                    navigate({ to: `/dataRequestFulfillment?${newParams.toString()}` });
                  }}
                />
              </div>
            </div>

            <div className="flex grow flex-col justify-start space-y-2 overflow-auto">
              <div className="min-h-[50vh] w-full flex-1">
                <MapComponent
                  id={MAP_ID}
                  showLayers
                  showLegend
                  showBasemapPicker
                  showZoomButtons
                  showScale
                  allowRotation
                  measureTool
                  allowHistoricalImagery={false}
                  allowSplitView={false}
                  showLayerDetails
                  initialViewState={initialViewState}
                  projectGeometry={current?.original.geometry}
                  mapStyle={MapStyle.civilGridLight}
                >
                  {popup && (
                    <Popup
                      key={`${popup.lngLat.lng},${popup.lngLat.lat}`}
                      longitude={popup.lngLat.lng}
                      latitude={popup.lngLat.lat}
                      onClose={() => setPopup(null)}
                      closeOnClick={true}
                      closeOnMove={true}
                      closeButton={false}
                    >
                      {popup.content}
                    </Popup>
                  )}
                  {isCriticalInfraVisible && <CriticalInfraPolygons setPopup={setPopup} />}

                  <BoundaryNote />
                </MapComponent>
              </div>

              <div className="grow space-y-2">
                <div className="flex w-full items-center justify-between space-x-4">
                  <FormSectionTitle>Project Info</FormSectionTitle>

                  <StatusButtons
                    current={current}
                    update={notesUpdate}
                    fetchUpdate={fetchUpdateNotes}
                    onNavigate={onNavigate}
                  />
                </div>

                <div className="flex justify-start">
                  <FlaggedWarning current={current} />
                </div>

                <div className="grid grid-cols-2 gap-2 gap-x-4">
                  <StatusSelect
                    current={current}
                    update={notesUpdate}
                    fetchUpdate={fetchUpdateNotes}
                    onNavigate={onNavigate}
                  />

                  <div className="relative w-full">
                    <Input
                      value={refId}
                      onChange={onRefIdChange}
                      label="External Ref ID"
                      error={
                        isRefIdError
                          ? 'An error occurred while updating the external ref ID, please contact support.'
                          : undefined
                      }
                      disabled={isRefIdError}
                    />

                    <Indicator visible={isRefIdLoading} position="bottom">
                      <CircularProgress indeterminate />
                    </Indicator>

                    <Indicator visible={isRefIdSuccess} position="bottom">
                      ✅
                    </Indicator>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 gap-x-4">
                  <div className="relative w-full">
                    <Input
                      label="Shared Link URL"
                      value={sharedLinkUrl}
                      canCopy
                      onChange={onSharedLinkUrlChange}
                      error={
                        isSharedLinkUrlError
                          ? 'An error occurred while updating the shared link URL, please contact support.'
                          : undefined
                      }
                      disabled={isSharedLinkUrlError}
                    />

                    <Indicator visible={isSharedLinkUrlLoading} position="bottom">
                      <CircularProgress indeterminate />
                    </Indicator>

                    <Indicator visible={isSharedLinkUrlSuccess} position="bottom">
                      ✅
                    </Indicator>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Input label="Project Name" value={values.name} readOnly canCopy />

                  <Input label="Client" value={values.client} readOnly canCopy />

                  <TextArea label="Scope" value={values.scope} readOnly canCopy />
                </div>

                <div className="grid grid-cols-2 gap-2 gap-x-4">
                  <Input
                    label="Start Date"
                    value={values.startDate?.toLocaleString()}
                    readOnly
                    canCopy={!!values.startDate}
                  />

                  <Input
                    label="End Date"
                    value={values.endDate?.toLocaleString()}
                    readOnly
                    canCopy={!!values.endDate}
                  />
                </div>

                <br />

                <div className="flex justify-start">
                  <ExportGeometryToKMZButton request={current.original} />
                </div>

                {!!values.isPublicProject && (
                  <>
                    <FormSectionTitle>Public Project Information</FormSectionTitle>

                    <div className="grid grid-cols-2 gap-2 gap-x-4">
                      {Object.keys(values.publicProjectInfo).map((field) => {
                        const v = values.publicProjectInfo[field];

                        if (v.type === 'string') {
                          return (
                            <Input
                              key={`ppi-string-field-${field}`}
                              label={v.label}
                              value={v.value?.toString()}
                              readOnly
                              canCopy
                            />
                          );
                        }

                        return null;
                      })}
                    </div>
                  </>
                )}
                <br />
                <br />
                <div className="flex w-full items-center justify-between space-x-4">
                  <FormSectionTitle>Requestor Details</FormSectionTitle>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const subject = `[Response Required] Map request for ${values.name}`;
                      const body = `Hi ${values.requestor.name},

We're contacting you regarding your recent map request for ${values.name}(${values.requestId}) that you submitted to ${currentOrganization?.name}.

Please reply to this email with the following information:
- 
- 

Thank you.`;

                      window.open(
                        `mailto:${values.requestor.email}?subject=${subject}&body=${encodeURIComponent(body)}`,
                      );
                    }}
                  >
                    Contact Requestor
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2 gap-x-4">
                  <Input label="Name" value={values.requestor.name} readOnly canCopy />

                  <Input label="Organization" value={values.requestor.organization} readOnly canCopy />

                  <Input label="Email" value={values.requestor.email} readOnly canCopy />

                  <Input label="Phone" value={values.requestor.phone} readOnly canCopy />
                </div>

                <Input label="Address" value={values.requestor.address.street} readOnly canCopy />

                <div className="grid grid-cols-3 gap-2 gap-x-4">
                  <Input label="City" value={values.requestor.address.city} readOnly canCopy />

                  <Input label="State" value={values.requestor.address.state} readOnly canCopy />

                  <Input label="ZIP Code" value={values.requestor.address.zipCode} readOnly canCopy />
                </div>

                <br />

                {!!config.current?.original.ceiiFile?.path && (
                  <>
                    <br />
                    <FormSectionTitle>Files Uploaded</FormSectionTitle>
                    <div className="flex w-full cursor-pointer items-center justify-between rounded p-1 hover:bg-brand/10">
                      <span>{config.current!.original.ceiiFile.name}</span>
                      <div className="flex gap-1">
                        <FileViewer file={config.current!.original.ceiiFile! as any} />

                        <Tooltip asChild text="Download file">
                          <IconButton
                            icon="download"
                            variant="hollow"
                            aria-label={`Download file ${config.current.original.ceiiFile.name}`}
                            data-testid="request-sheet-download-ceii-file"
                            onClick={() => downloadCeiiFile(config.current!.original.ceiiFile!, config.current?.id)}
                          />
                        </Tooltip>
                      </div>
                    </div>
                    <br />
                  </>
                )}

                <br />

                <FormSectionTitle>Internal Notes</FormSectionTitle>
                <div className="relative grid grid-cols-1">
                  <TextArea
                    label=""
                    value={internalNotes}
                    onChange={onNotesChange}
                    disabled={isNotesError}
                    error={
                      isNotesError
                        ? 'An error occurred while updating the internal notes, please contact support.'
                        : undefined
                    }
                  />

                  <Indicator visible={isNotesLoading}>
                    <CircularProgress indeterminate />
                  </Indicator>

                  <Indicator visible={isNotesSuccess}>✅</Indicator>
                </div>

                <br />
                <br />

                <FormSectionTitle>Payment Details</FormSectionTitle>
                <div>
                  <Input
                    label="Payment Method"
                    readOnly
                    value={paymentMethodLabels[current?.original.paymentMethod?.type ?? 'other']}
                  />
                </div>

                <br />
                <br />
                <FormSectionTitle>Activity Feed</FormSectionTitle>

                <div className="relative flex w-full flex-col space-y-2 rounded-sm border p-2">{activityItems}</div>
              </div>
            </div>
          </div>
        );
      }}
    </Sheet>
  );
}

function Indicator({
  visible,
  children,
  position = 'top',
}: {
  visible: boolean;
  children?: React.ReactNode;
  position?: 'top' | 'bottom';
}) {
  if (!visible) return null;

  return <div className={classNames('absolute right-5', position === 'bottom' ? 'bottom-2' : 'top-1')}>{children}</div>;
}

function FlaggedWarning({ current }: { current?: Row<MapFulfillmentRequest> }) {
  const requestFlags = current?.original.requestFlags;
  const hasFlags = !!requestFlags?.length;

  if (!hasFlags) return null;

  return (
    <div className="rounded border border-red-400 p-2 text-xs">
      ⚠️ This request needs manual review for the following reason(s):
      <ul className="list-inside list-disc">
        {requestFlags.map((f) => {
          return (
            <li key={`flag-${f}`} className="list-item">
              {requestFlagLabels[f] ?? f}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
